import React, { Suspense, memo, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, CircularProgress } from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import theme from "@/theme/theme";
import { AuthProvider, useAuth } from "@/context/AuthProvider";
import { AudioProvider } from "@/context/AudioProvider";
import MainLayout from "./layouts/MainLayout";
import "./App.css";
import SearchPage from './pages/search';
import ArtistIdRedirectPage from './pages/artist/[id]';
import ArtistPageBySlug from './pages/artist/[slug]';
import AlbumPageBySlug from './pages/album/[slug]';
// @ts-ignore: No type declarations for react-helmet
import { Helmet } from "react-helmet";
import JoinPage from './pages/join';
import FanWaitlistPage from './pages/fan-waitlist';
import SupportPage from './pages/support';
import GenrePage from './pages/GenrePage';
import ArtistRights from "./pages/artist-rights";
import PrivacyPolicy from "./pages/privacy-policy";
import TermsOfService from "./pages/terms-of-service";
import PaymentSuccessPage from './pages/payment-success';
import PaymentCancelledPage from './pages/payment-cancelled';
import AlbumCheckoutPage from './pages/checkout/album/[id]';
import ScrollToTop from './components/ScrollToTop';
import SubscribePage from './pages/subscribe';
import TestMassiveStream from './pages/TestMassiveStream';

// Lazy-loaded pages to optimize performance
const HomePage = lazy(() => import("./pages/HomePage"));
const HomePage2 = lazy(() => import("./pages/HomePage2"));
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const DiscoverPage = lazy(() => import("./pages/DiscoverPage"));
const UpdatePassword = lazy(() => import("./pages/auth/UpdatePassword"));
const Dashboard = lazy(() => import("./pages/user/Dashboard"));
const NotFound = lazy(() => import("./pages/404"));
const BlockedPage = lazy(() => import("./pages/Blocked"));
const MixtapeLibrary = lazy(() => import("./pages/mixtapes/MixtapeLibrary"));
const MixtapeDetail = lazy(() => import("./pages/mixtapes/MixtapeDetail"));

/** PrivateRoute Component: Redirects to login if user isn't authenticated */
const PrivateRoute = memo(({ element }: { element: React.ReactElement }) => {
  const { user, loading, hasActiveSubscription, userRole } = useAuth();
  const location = useLocation();
  console.log("PrivateRoute - Authentication status:", { isAuthenticated: !!user, loading, user });
  
  // Show loading indicator while checking authentication
  if (loading) {
    return <CircularProgress sx={{ display: "block", margin: "auto", mt: 4 }} />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const role = userRole?.toLowerCase() || "";
  const subscriptionBypass = ["admin", "superadmin", "artist"].includes(role);

  if (!subscriptionBypass && !hasActiveSubscription && location.pathname !== "/subscribe") {
    return <Navigate to="/subscribe" replace />;
  }

  return element;
});

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Helmet>
        <script src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit" async defer></script>
      </Helmet>
      <Router>
        <ScrollToTop />
        <AuthProvider>
          <AudioProvider>
            <div className="app-container">
              {/* Toast Notifications */}
              <ToastContainer position="top-right" autoClose={5000} hideProgressBar />
              
              <Suspense fallback={<CircularProgress sx={{ display: "block", margin: "auto", mt: 4 }} />}>
              <Routes>
              {/* New Marketing Homepage - Standalone (No Layout) */}
              <Route path="/" element={<HomePage2 />} />
              
              {/* Old Homepage - Backup (No Layout) */}
              <Route path="/old-home" element={<HomePage />} />
              
              {/* All other routes wrapped in MainLayout */}
              <Route element={<MainLayout />}>
                {/* Public Routes */}
                <Route path="login" element={<Login />} />
                <Route path="register" element={<Register />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="update-password" element={<UpdatePassword />} />
                <Route path="join" element={<JoinPage />} />
                <Route path="fan-waitlist" element={<FanWaitlistPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="subscribe" element={<SubscribePage />} />
                <Route path="genre/:slug" element={<GenrePage />} />
                <Route path="artist-rights" element={<ArtistRights />} />
                <Route path="privacy-policy" element={<PrivacyPolicy />} />
                <Route path="terms-of-service" element={<TermsOfService />} />
                <Route path="payment-success" element={<PaymentSuccessPage />} />
                <Route path="payment-cancelled" element={<PaymentCancelledPage />} />
                <Route path="checkout/album/:id" element={<AlbumCheckoutPage />} />
                <Route path="test-massive-stream" element={<TestMassiveStream />} />
                
                {/* Protected Routes */}
                <Route path="discover" element={<PrivateRoute element={<DiscoverPage />} />} />
                <Route path="dashboard" element={<PrivateRoute element={<Dashboard />} />} />
                <Route path="search" element={<PrivateRoute element={<SearchPage />} />} />
                <Route path="mixtapes" element={<PrivateRoute element={<MixtapeLibrary />} />} />
                <Route path="mixtape/:id" element={<PrivateRoute element={<MixtapeDetail />} />} />
                
                {/* Hierarchical Album Routes - Must come before Artist Routes for proper matching */}
                <Route path="artist/:artistIdentifier/album/:albumIdentifier" element={<PrivateRoute element={<AlbumPageBySlug />} />} />
                
                {/* Artist Routes */}
                <Route path="artist/:artistIdentifier" element={<PrivateRoute element={<ArtistIdRedirectPage />} />} />
              
                {/* Legacy Album Routes (for backward compatibility) */}
                <Route path="/album/:id([a-f0-9\-]{36})" element={<PrivateRoute element={<AlbumPageBySlug />} />} />
                <Route path="/album/:slug" element={<PrivateRoute element={<AlbumPageBySlug />} />} />
                
                {/* 404 Route */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
              </Suspense>
              
            </div>
          </AudioProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
